import { unifiedApi } from "@/common";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { FLAGS, LAST_API_FETCH_INTERVAL } from "@/define";
import { logEvent } from "@/common/logger";
import { defineStore } from "pinia";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { computed, ref } from "vue";
import { openTextBrands, paymentTypes } from "@/components/Buy/BuyEnums";
import { addBuyFlowLogEntry } from "@/common/buyflowTracker";
import { IPurchaseError } from "@/components/Buy/Interfaces";
import {
  hideExpiredTrial,
  isSafeProduct,
  isWebrootProduct,
  outstandingSubscriptionExpired,
} from "@/common/subscriptionHelper";
import { carboniteSkus } from "@/common/productsBySku";
import {
  subscriptionIsNotExpired,
  subscriptionIsNotRenewable,
} from "@/components/SubscriptionsV2/SubscriptionV2Helper";

export const useSubscriptionsStore = defineStore("subscriptions", () => {
  const subscriptions = ref<ISubscription[]>([]);
  const lastRequestTime = ref(0);
  const isRunning = ref(false);
  const selectedSubscription = ref<ISubscription | undefined>(undefined);

  const outStandingSubscriptions = computed(() => {
    return subscriptions.value.filter(sub => sub.hasOutstandingInvoices && sub.outstandingInvoice);
  });

  const hasOutstandingInvoice = computed(() => {
    return (
      outStandingSubscriptions.value.length > 0 &&
      outStandingSubscriptions.value.some(sub => !outstandingSubscriptionExpired(sub))
    );
  });

  const selectedSubscriptionBrand = computed(() => {
    if (carboniteSkus.find(sku => sku === selectedSubscription.value?.sku)) {
      return openTextBrands.Carbonite;
    }

    return openTextBrands.Webroot;
  });

  const subsWithoutExpiredTrials = computed(() => {
    //Get rid of expired Trials
    return subscriptions.value.filter(subscription => !hideExpiredTrial(subscription));
  });

  const activeSubscriptions = computed(() => {
    return subsWithoutExpiredTrials.value.filter(sub => subscriptionIsNotExpired(sub));
  });

  const nonrenewableSubscriptions = computed(() => {
    return subsWithoutExpiredTrials.value.filter(sub => subscriptionIsNotRenewable(sub));
  });

  const renewableSubscriptions = computed(() => {
    return subsWithoutExpiredTrials.value.filter(
      sub => !subscriptionIsNotRenewable(sub) && !subscriptionIsNotExpired(sub)
    );
  });

  const isSelectedSubscriptionWebroot = computed(() => {
    return FLAGS.ENABLE_BUYFLOW_PAYPAL && selectedSubscription.value && isWebrootProduct(selectedSubscription.value);
  });

  const isSelectedSubscriptionCarbonite = computed(() => {
    return (
      FLAGS.ENABLE_BUYFLOW_PAYPAL_FOR_CARBONITE === "true" &&
      selectedSubscription.value &&
      isSafeProduct(selectedSubscription.value)
    );
  });

  const isAutoRenewEnabled = computed(() => {
    return selectedSubscription.value?.isAutoRenewEnabled ?? false;
  });

  //This replaces get subscriptions going forward.
  //Separating the call to get the data and the return of the data is easier to unit test
  async function populateSubscriptions(isFromBuyFlow = false) {
    try {
      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call start: get subscriptions");
      }
      // Waits for the current call to finish before trying and get aborted (in case a simultaneous call)
      while (isRunning.value) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const currentTime = new Date().getTime();
      if (currentTime - lastRequestTime.value >= LAST_API_FETCH_INTERVAL) {
        lastRequestTime.value = new Date().getTime();
        isRunning.value = true;
        const [getSubscriptions, getPaymentMethods] = await Promise.all([
          unifiedApi.getLatestSubscriptions(),
          unifiedApi.getUserPaymentMethods(),
        ]);
        subscriptions.value = getSubscriptions.data ?? [];
        const userPaymentMethods = getPaymentMethods.data;
        subscriptions.value.forEach(sub => {
          const paymentMethod = userPaymentMethods.paymentMethods.find(x => x.id == sub.zuoraPaymentMethodId);
          if (paymentMethod?.cardType !== paymentTypes.PayPal) {
            sub.paymentInfo = {
              cardLastFourDigits: paymentMethod?.lastDigits ?? "",
              cardType: paymentMethod?.cardType ?? "",
              paymentType: paymentMethod?.paymentMethodType ?? "",
            };
          } else {
            sub.paymentInfo = {
              payPalEmail: paymentMethod.email,
              cardType: paymentTypes.PayPal,
            };
          }
        });

        isRunning.value = false;
        logEvent("subscriptions state updated", "subscriptions store", subscriptions.value);
      }

      if (isFromBuyFlow) {
        addBuyFlowLogEntry("API call end: get subscriptions");
      }
    } catch (err) {
      const error = err as AxiosError;
      if (isFromBuyFlow) {
        const response = error?.response?.data as IPurchaseError;
        addBuyFlowLogEntry(`API error: get subscriptions. ${response.message}`);
      }
      //redirect to error page
      handleApiError(error, false);
    }
  }

  //If the user has updated their subscriptions off site we want to refresh the store next time they come to the page
  function forceRefresh() {
    lastRequestTime.value = new Date().getTime() - LAST_API_FETCH_INTERVAL;
    logEvent("Force Subscriptions store refresh");
  }

  async function forceRefreshAndPopulateSubscriptions() {
    forceRefresh();
    await populateSubscriptions();
  }

  return {
    subscriptions,
    lastRequestTime,
    isRunning,
    outStandingSubscriptions,
    hasOutstandingInvoice,
    selectedSubscription,
    selectedSubscriptionBrand,
    subsWithoutExpiredTrials,
    isSelectedSubscriptionWebroot,
    isSelectedSubscriptionCarbonite,
    isAutoRenewEnabled,
    activeSubscriptions,
    nonrenewableSubscriptions,
    renewableSubscriptions,
    forceRefresh,
    forceRefreshAndPopulateSubscriptions,
    populateSubscriptions,
  };
});
